import React, { useState, useEffect } from "react";
import { useParams } from "react-router";

import { ProjectApplicationDetail } from "./ProjectApplicationDetail";

import { SonicMessage } from "../../../../common/classes/SonicMessage";

import { getMessagesAdmin } from "../../../../services/api/chat/getMessagesAdmin";

import { useAppSelector } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";
import {
  useGetApplication,
  useUpdateApplication,
} from "../../../../utils/useApplication";
import { AdminChatBoxType } from "../../../chat/AdminChatBox";
import { ChatErrorType } from "../../../../common/classes/SonicChatErrorType";
import { postCreateChatroomAdmin } from "../../../../services/api/chat/postCreateChatroomAdmin";
import Modal from "../../../../common/management/Modal";
import { CategoryState } from "../../../../utils/CategoryState";
import Checkbox from "../../../../common/forms/Checkbox";
import { OfferStatus } from "../../../../utils/OfferStatus";

type Props = {
  isSelect: boolean;
};

const ProjectApplicationDetailContainer: React.FC<Props> = ({ isSelect }) => {
  const { id } = useParams<{ id: string }>();
  const auth_token = useAppSelector(getAuthToken);

  const {
    applicationData: { applications, projects },
    getApplicationLoading,
  } = useGetApplication({
    auth_token,
    is_jikei_request: !isSelect,
    id,
  });

  const { updateApplication, updateLoading } = useUpdateApplication();

  // Status component
  const [selectStatus, setSelectStatus] = useState<string>();
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);
  const [sendMail, setSendMail] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleUpdateStatus = () => {
    if (auth_token && id && selectStatus) {
      updateApplication({
        auth_token,
        is_jikei_request: !isSelect,
        application_id: id,
        status: selectStatus,
        send_mail: sendMail,
      });
      setShowStatusMenu(!showStatusMenu);
    }
  };

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  // UserNote component
  const [isMemoEdit, setMemoEdit] = useState<boolean>(false);
  const [memo, setMemo] = useState<string>();

  const handleMemoEdit = () => {
    setMemoEdit(true);
  };

  const handleMemoUpdate = () => {
    if (auth_token && id) {
      updateApplication({
        auth_token,
        is_jikei_request: !isSelect,
        application_id: id,
        memo: memo,
      });
      setMemoEdit(false);
    }
  };

  // Chat
  const [messages, setMessages] = useState<Array<SonicMessage>>([]);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [chatboxType, setChatboxType] = useState<AdminChatBoxType>(
    AdminChatBoxType.Loading
  );

  const handleLoad = async (page: number) => {
    if (!auth_token || !hasNext || !id) return;

    const res = await getMessagesAdmin(
      auth_token,
      applications[0]?.applicant_id,
      projects[0]?.owner_id,
      page,
      isSelect
    );
    if (typeof res !== "object") return;

    setMessages((prevMessages) => [...res.messageHistory, ...prevMessages]);
    setHasNext(res.hasNext);
  };

  useEffect(() => {
    const fetchMessages = async () => {
      if (auth_token) {
        const res = await getMessagesAdmin(
          auth_token,
          applications[0]?.applicant_id,
          projects[0]?.owner_id,
          1,
          isSelect
        );

        if (typeof res === "object") {
          setMessages(res.messageHistory);
          setHasNext(res.hasNext);
          setChatboxType(AdminChatBoxType.ShowMessages);
          return;
        }

        switch (res) {
          case ChatErrorType.RoomNotFound:
            setChatboxType(AdminChatBoxType.ReCreate);
            break;
          default:
            setChatboxType(AdminChatBoxType.Error);
            break;
        }
        return;
      }
    };

    if (projects && applications[0]?.id === id) {
      fetchMessages();
    }
  }, [projects]);

  // ルーム再作成
  const handleReCreateRoom = async () => {
    if (auth_token && applications?.[0] && projects?.[0]) {
      setChatboxType(AdminChatBoxType.ReCreating);

      const res = await postCreateChatroomAdmin(
        auth_token,
        applications[0].applicant_id,
        projects[0].owner_id,
        "project",
        applications[0].project_id,
        applications[0].timestamp * 1000
      );

      if (res === null) {
        return setChatboxType(AdminChatBoxType.FailedReCreate);
      }

      return setChatboxType(AdminChatBoxType.ReCreated);
    }
  };

  return (
    <>
      <ProjectApplicationDetail
        isSelect={isSelect}
        isLoading={getApplicationLoading || updateLoading}
        application={applications[0]}
        projects={projects[0]}
        showStatusMenu={showStatusMenu}
        handleUpdateStatus={
          selectStatus === OfferStatus.Complete ||
          selectStatus === OfferStatus.Cancelled
            ? () => {
                setShowModal(true);
              }
            : handleUpdateStatus
        }
        handleStatusMenu={handleStatusMenu}
        selectStatus={selectStatus}
        setSelectStatus={setSelectStatus}
        isMemoEdit={isMemoEdit}
        handleMemoEdit={handleMemoEdit}
        handleMemoUpdate={handleMemoUpdate}
        setMemo={setMemo}
        messages={messages}
        hasNext={hasNext}
        handleLoad={handleLoad}
        chatboxType={chatboxType}
        handleReCreateRoom={handleReCreateRoom}
      />
      {showModal ? (
        <Modal
          closeModal={() => {
            setShowModal(false);
          }}
          handler={() => {
            setShowModal(false);
            handleUpdateStatus();
          }}
          categoryState={CategoryState.Confirm}
          headText="確認"
          buttonText="送信"
        >
          <Checkbox
            id="send-mail-check"
            name="send-mail-checkbox"
            checkHandler={() => {
              setSendMail(true);
            }}
            uncheckHandler={() => {
              setSendMail(false);
            }}
            checked={sendMail}
            text="ユーザーにメールを送信する"
          />
        </Modal>
      ) : null}
    </>
  );
};

export default ProjectApplicationDetailContainer;
